import Select from "./Select";
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCalendar, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Button from "./Button";
import "./DatePicker.css";

export interface DatePickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
}

/** API dates are calendar dates, never timezone-converted timestamps. */
export function parseFilterDate(value?: string): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}
export function formatFilterDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
const displayDate = (value: string) => parseFilterDate(value) ? value.split("-").reverse().join("/") : "";
const months = Array.from({ length: 12 }, (_, index) => new Date(2026, index, 1).toLocaleString("en-GB", { month: "long" }));
const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export default function DatePicker({ label, value, onChange, min, max, disabled }: DatePickerProps) {
  const id = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => displayDate(value));
  const [invalid, setInvalid] = useState(false);
  const [focused, setFocused] = useState(() => value || formatFilterDate(new Date()));
  const [month, setMonth] = useState(() => parseFilterDate(value) || new Date());
  const [position, setPosition] = useState<React.CSSProperties>({ visibility: "hidden" });
  useEffect(() => { setText(displayDate(value)); setInvalid(false); }, [value]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  const allowed = (date: string) => (!min || date >= min) && (!max || date <= max);
  const today = formatFilterDate(new Date());
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };
  const choose = (date: string) => {
    if (date && !allowed(date)) return;
    onChange(date); setText(displayDate(date)); setInvalid(false); close(true);
  };
  const show = () => {
    if (disabled) return;
    const next = value || (min && today < min ? min : max && today > max ? max : today);
    setMonth(parseFilterDate(next) || new Date()); setFocused(next); setOpen(true);
  };
  const commit = () => {
    if (!text.trim()) { setInvalid(false); if (value) onChange(""); return; }
    const parts = text.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    const next = parts ? `${parts[3]}-${parts[2]}-${parts[1]}` : "";
    if (!parseFilterDate(next) || !allowed(next)) { setInvalid(true); return; }
    setInvalid(false); if (next !== value) onChange(next);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      if (!anchor.current || !dialog.current) return;
      const rect = anchor.current.getBoundingClientRect();
      const height = dialog.current.getBoundingClientRect().height;
      const width = Math.min(320, window.innerWidth - 24);
      const below = window.innerHeight - rect.bottom;
      setPosition({ position: "fixed", width, left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), top: below >= height + 12 ? rect.bottom + 8 : Math.max(12, rect.top - height - 8), visibility: "visible" });
    };
    const outside = (event: MouseEvent) => {
      if (!anchor.current?.contains(event.target as Node) && !dialog.current?.contains(event.target as Node)) setOpen(false);
    };
    update(); window.addEventListener("resize", update); window.addEventListener("scroll", update, true); document.addEventListener("mousedown", outside);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); document.removeEventListener("mousedown", outside); };
  }, [open, month]);
  useEffect(() => {
    if (open) dialog.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus();
  }, [open, focused, month]);

  const year = month.getFullYear(), monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const offset = (first.getDay() + 6) % 7;
  const cells = Array.from({ length: 42 }, (_, index) => new Date(year, monthIndex, index - offset + 1));
  const monthAllowed = (date: Date) => (!min || formatFilterDate(new Date(date.getFullYear(), date.getMonth() + 1, 0)) >= min) && (!max || formatFilterDate(new Date(date.getFullYear(), date.getMonth(), 1)) <= max);
  const navigateMonth = (next: Date) => {
    if (!monthAllowed(next)) return;
    setMonth(next);
    const firstDay = formatFilterDate(new Date(next.getFullYear(), next.getMonth(), 1));
    setFocused(min && firstDay < min ? min : firstDay);
  };
  const onCalendarKey = (event: React.KeyboardEvent<HTMLButtonElement>, date: Date) => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -((date.getDay() + 6) % 7), End: 6 - ((date.getDay() + 6) % 7) };
    let next: Date;
    if (event.key in moves) next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + moves[event.key]);
    else if (event.key === "PageUp" || event.key === "PageDown") {
      const first = new Date(date.getFullYear(), date.getMonth() + (event.key === "PageUp" ? -1 : 1), 1);
      next = new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()));
    } else return;
    event.preventDefault();
    const nextValue = formatFilterDate(next);
    if (allowed(nextValue)) { setFocused(nextValue); setMonth(next); }
  };
  const trapFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(true); }
    if (event.key !== "Tab") return;
    const nodes = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled):not([tabindex="-1"]), select:not(:disabled):not([tabindex="-1"])') || []);
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const minYear = parseFilterDate(min)?.getFullYear() ?? Math.min(1900, year);
  const maxYear = parseFilterDate(max)?.getFullYear() ?? Math.max(new Date().getFullYear() + 20, year);

  return <div className="navo-date-picker" ref={anchor}>
    <label className="navo-field" htmlFor={id}><span>{label}</span>
      <span className="navo-date-picker__input-wrap">
        <input id={id} className="navo-field__input" type="text" inputMode="numeric" autoComplete="off" placeholder="DD/MM/YYYY" maxLength={10}
          value={text} disabled={disabled} aria-invalid={invalid || undefined} aria-describedby={invalid ? `${id}-error` : undefined}
          onChange={event => { setText(event.target.value); setInvalid(false); }} onBlur={commit}
          onKeyDown={event => { if (event.key === "ArrowDown") { event.preventDefault(); show(); } if (event.key === "Enter") { event.preventDefault(); commit(); } }} />
        <Button ref={trigger} type="button" className="navo-date-picker__trigger" disabled={disabled} aria-label={`Choose ${label.toLowerCase()} date`}
          aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-calendar` : undefined} onClick={() => open ? close() : show()}><FiCalendar aria-hidden="true" /></Button>
      </span>
    </label>
    {invalid && <span id={`${id}-error`} className="navo-date-picker__error" role="alert">Use DD/MM/YYYY{min || max ? " within the date range" : ""}.</span>}
    {open && createPortal(<div ref={dialog} id={`${id}-calendar`} className="navo-calendar" role="dialog" aria-modal="true" aria-label={`Choose ${label.toLowerCase()} date`} style={position} onKeyDown={trapFocus}>
      <div className="navo-calendar__header">
        <Button aria-label="Previous month" disabled={!monthAllowed(new Date(year, monthIndex - 1, 1))} onClick={() => navigateMonth(new Date(year, monthIndex - 1, 1))}><FiChevronLeft /></Button>
        <div className="navo-calendar__month-select">
          <Select aria-label="Month" value={monthIndex} onChange={event => navigateMonth(new Date(year, Number(event.target.value), 1))}>{months.map((name, index) => <option key={name} value={index} disabled={!monthAllowed(new Date(year, index, 1))}>{name}</option>)}</Select>
          <Select aria-label="Year" value={year} onChange={event => { const nextYear = Number(event.target.value); const target = new Date(nextYear, monthIndex, 1); navigateMonth(monthAllowed(target) ? target : parseFilterDate(nextYear === minYear ? min : max) || target); }}>{Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index).map(value => <option key={value} value={value}>{value}</option>)}</Select>
        </div>
        <Button aria-label="Next month" disabled={!monthAllowed(new Date(year, monthIndex + 1, 1))} onClick={() => navigateMonth(new Date(year, monthIndex + 1, 1))}><FiChevronRight /></Button>
      </div>
      <div className="navo-calendar__month-title" aria-live="polite">{months[monthIndex]} {year}</div>
      <table role="grid" aria-label={`${months[monthIndex]} ${year}`} className="navo-calendar__grid"><thead><tr>{weekdays.map(day => <th key={day} scope="col">{day}</th>)}</tr></thead><tbody>
        {Array.from({ length: 6 }, (_, row) => <tr key={row}>{cells.slice(row * 7, row * 7 + 7).map(date => {
          const key = formatFilterDate(date), outside = date.getMonth() !== monthIndex;
          return <td key={key} aria-selected={key === value}><button type="button" data-date={key}
            className={`navo-calendar__day${outside ? " is-outside" : ""}${key === value ? " is-selected" : ""}${key === today ? " is-today" : ""}`}
            aria-label={date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} aria-current={key === today ? "date" : undefined}
            tabIndex={key === focused ? 0 : -1} disabled={!allowed(key)} onFocus={() => setFocused(key)} onKeyDown={event => onCalendarKey(event, date)} onClick={() => choose(key)}>{date.getDate()}</button></td>;
        })}</tr>)}
      </tbody></table>
      <div className="navo-calendar__footer"><Button variant="text" size="small" onClick={() => choose("")}>Clear</Button><Button variant="text" size="small" disabled={!allowed(today)} onClick={() => choose(today)}>Today</Button><Button size="small" onClick={() => close(true)}>Close</Button></div>
    </div>, anchor.current?.closest("dialog") || document.body)}
  </div>;
}
